import Editor, { EditorProps, loader, type Monaco } from "@monaco-editor/react";
import { registerGroovyHoverProvider } from "@via/components/code-editor/groovy-hover-provider.ts";
import { registerGroovyTokens } from "@via/components/code-editor/groovy.ts";
import { monaco } from "@via/components/code-editor/monaco.ts";
import codicon from "@via/components/monaco-editor/codicon.ttf";
import "@via/components/monaco-editor/monaco-editor.css";
import { EDITOR_HEIGHT, LANGUAGE_MODES } from "@via/utils/constants.tsx";
import { useEffect } from "react";

loader.config({ monaco });
registerGroovyHoverProvider(monaco, LANGUAGE_MODES.GROOVY);

export type MonacoEditorProps = EditorProps & {
  editorRef: React.MutableRefObject<any>;
};

const loadMyFont = async () => {
  if (!("fonts" in document)) return; // older browsers
  const ff = new FontFace("codicon", `url(${codicon}) format("truetype")`, {
    style: "normal",
  });
  await ff.load();
  document.fonts.add(ff);
};

export default function MonacoEditor(props: MonacoEditorProps): JSX.Element {
  const { editorRef, onMount, ...rest } = props;

  const onEditorDidMount = (
    editor: monaco.editor.IStandaloneCodeEditor,
    _monaco: Monaco
  ): void => {
    if (editorRef) {
      editorRef.current = editor;
    }
    onMount?.(editor, _monaco);
  };

  useEffect(() => {
    if (monaco) {
      monaco.languages.register({ id: LANGUAGE_MODES.GROOVY });
      monaco.languages.setMonarchTokensProvider(
        LANGUAGE_MODES.GROOVY,
        registerGroovyTokens()
      );

      // Configure comment rules for Groovy
      monaco.languages.setLanguageConfiguration(LANGUAGE_MODES.GROOVY, {
        comments: {
          lineComment: "//",
          blockComment: ["/*", "*/"],
        },
        brackets: [
          ["{", "}"],
          ["[", "]"],
          ["(", ")"],
        ],
      });

      loadMyFont();
    }
  }, [monaco]);

  return <Editor onMount={onEditorDidMount} height={EDITOR_HEIGHT} {...rest} />;
}
